import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddEnginePowerMutation, useGetCategoriesQuery, useUpdateEnginePowerMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  enginePowerData?: any;
}

export default function AddEnginePower({
  open,
  onClose,
  setOpen,
  enginePowerData
}: ModalProps) {

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<any>({});

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery(
    { page: 1, limit: 100, type: selectedVehicleType || undefined },
    { skip: !selectedVehicleType }
  );
  const [addEnginePower, { isLoading: isAdding }] = useAddEnginePowerMutation();
  const [updateEnginePower, { isLoading: isUpdating }] = useUpdateEnginePowerMutation();

  useEffect(() => {
    if (enginePowerData && open) {
      setName(enginePowerData.name);
      setSelectedCategories(enginePowerData.category?.map((c: any) => c._id) || []);
      const vType = enginePowerData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!enginePowerData && open) {
      // Reset form
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [enginePowerData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }

    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Engine Power is required / قوة المحرك مطلوبة";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    const payload = {
      name,
      categoryId: selectedCategories,
      type: Number(selectedVehicleType),
    };

    try {
      if (enginePowerData?._id) {
        await updateEnginePower({ id: enginePowerData._id, ...payload }).unwrap();
        toast.success("Engine Power updated successfully / تم تحديث قوة المحرك بنجاح");
      } else {
        await addEnginePower(payload).unwrap();
        toast.success("Engine Power added successfully / تمت إضافة قوة المحرك بنجاح");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong / حدث خطأ ما");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{enginePowerData ? "Edit Engine Power / تعديل قوة المحرك" : "Add Engine Power / إضافة قوة محرك"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group">
              <label htmlFor="engine-power-name">Engine Power / قوة المحرك</label>
              <TextField
                id="engine-power-name"
                placeholder="Enter Power (e.g., 150 HP) / أدخل القوة (مثال: 150 حصان)"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category / الفئة</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = (selected as string[]) || [];
                  if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                  return selectedIds.map(id => {
                    // 1. Try to find in loaded categories list
                    const name = categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name;
                    if (name) return name;

                    // 2. Try to find in enginePowerData.category (array of objects)
                    if (enginePowerData?.category && Array.isArray(enginePowerData.category)) {
                      const initialCat = enginePowerData.category.find((cat: any) => cat._id === id);
                      if (initialCat?.name) return initialCat.name;
                    }

                    // 3. Try to use categoryName by index matching
                    if (enginePowerData?.categoryId && enginePowerData?.categoryName) {
                      if (Array.isArray(enginePowerData.categoryId) && Array.isArray(enginePowerData.categoryName)) {
                        const index = enginePowerData.categoryId.indexOf(id);
                        if (index !== -1 && enginePowerData.categoryName[index]) return enginePowerData.categoryName[index];
                      } else if (enginePowerData.categoryId === id) {
                        return Array.isArray(enginePowerData.categoryName) ? enginePowerData.categoryName[0] : enginePowerData.categoryName;
                      }
                    }

                    return id;
                  }).join(", ");
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
                disabled={!selectedVehicleType}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>
                  {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                </MenuItem>
                {categoriesData?.data?.data?.length > 0 && (
                  <MenuItem value="all">
                    <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                  </MenuItem>
                )}
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={selectedCategories.includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
